import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, HardHat, Compass, Hammer, MapPin } from "lucide-react";
import Seo from "@/components/site/Seo";
import { LOCAL_BUSINESS_JSONLD } from "@/components/site/Seo";
import { CLIENT_PROJECTS, UK_HERO_IMG, UK_HERO_OBJECT_POSITION } from "@/data/projects";
import TrustBadges from "@/components/site/TrustBadges";
import FaqSection, { HOME_FAQS, faqJsonLd } from "@/components/site/FaqSection";
import BeforeAfterSlider from "@/components/site/BeforeAfterSlider";
import GoogleReviews, { REVIEWS_JSONLD } from "@/components/site/GoogleReviews";
import api from "@/lib/api";

const services = [
  { icon: HardHat, title: "House Extensions", slug: "house-extensions-london", desc: "Side-return, wrap-around and rear extensions designed for London family life." },
  { icon: Compass, title: "Loft Conversions", slug: "loft-conversions-london", desc: "Dormer, mansard and L-shape loft conversions — turn unused roof into a master suite." },
  { icon: Hammer, title: "Full Renovations", slug: "full-house-renovations-london", desc: "Whole-home refurbishments delivered under one contract and one project lead." },
  { icon: ShieldCheck, title: "New Builds & More", slug: "new-build-contractors-london", desc: "Bespoke new builds, kitchens, bathrooms and structural work — every service in-house." },
];

const AREAS = [
  { slug: "ealing", name: "Ealing", code: "W5/W13" },
  { slug: "richmond", name: "Richmond", code: "TW9/TW10" },
  { slug: "chiswick", name: "Chiswick", code: "W4" },
  { slug: "wimbledon", name: "Wimbledon", code: "SW19" },
  { slug: "hampstead", name: "Hampstead", code: "NW3" },
  { slug: "croydon", name: "Croydon", code: "CR0/CR2" },
];

// Pair before/after by project stage. All images are real Prime Build sites.
const byId = (id) => CLIENT_PROJECTS.find((p) => p.id === id);
const beforeAfterItems = [
  {
    before: byId("hampstead-newbuild-shell-joists").img,
    after: byId("hampstead-newbuild-buff-facade").img,
    beforeLabel: "Shell & Joists",
    afterLabel: "Completed Facade",
    caption: "Hampstead New Build — Shell to Buff Brick Facade",
    location: "Hampstead, NW3",
  },
  {
    before: byId("wembley-side-return-opening").img,
    after: byId("ealing-completed-extension").img,
    beforeLabel: "Structural Opening",
    afterLabel: "Completed Extension",
    caption: "Side-Return Extension — Structural to Finish",
    location: "London · Side-Return Extensions",
  },
];

export default function Home() {
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    api
      .get("/projects/featured")
      .then((r) => Array.isArray(r.data) && setFeatured(r.data))
      .catch(() => setFeatured([]));
  }, []);

  // Use admin featured projects if any exist, otherwise the first 3 built-in projects
  const featuredList = (featured && featured.length > 0 ? featured : CLIENT_PROJECTS.slice(0, 3)).slice(0, 3);

  return (
    <div data-testid="home-page">
      <Seo
        title="Premium Domestic Construction Company in London"
        description="Prime Build Design LTD — premium domestic builders in London. 10+ years of new builds, renovations and extensions across Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead & Croydon. Free AI cost estimate."
        path="/"
        jsonLd={[LOCAL_BUSINESS_JSONLD, REVIEWS_JSONLD, faqJsonLd(HOME_FAQS)]}
      />

      {/* HERO */}
      <section className="relative bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-6">
            <div className="overline mb-6">Domestic Construction · London · Since 2014</div>

            {/* Availability badge — premium, subtle, conversion-led */}
            <div
              className="inline-flex items-center gap-2.5 bg-white border border-slate-200 pl-2.5 pr-4 py-1.5 mb-7"
              data-testid="hero-availability-badge"
              aria-label="Currently booking premium projects for 2026"
            >
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#C5A059] opacity-60 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C5A059]" />
              </span>
              <span className="text-[11px] tracking-[0.18em] uppercase font-medium text-slate-700">
                Currently booking 2026 projects
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-[1.02] tracking-tight">
              We build <span className="italic font-medium">homes</span><br />
              that quietly outlast<br />
              <span className="text-[#C5A059]">trends.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              A decade of craftsmanship from foundations to finishes — Prime Build Design partners
              with London homeowners to deliver new builds, full renovations and extensions across
              Wembley, Ealing, Richmond, Chiswick, Wimbledon, Hampstead and Croydon.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/estimator" className="btn-accent" data-testid="hero-cta-estimator">
                Get Instant AI Estimate <ArrowUpRight size={18} />
              </Link>
              <Link to="/projects" className="btn-ghost" data-testid="hero-cta-projects">
                View Our Work
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
              {[
                ["10+", "Years building"],
                ["120+", "Homes delivered"],
                ["98%", "Client referrals"],
              ].map(([n, l]) => (
                <div key={l} className="border-l border-slate-300 pl-4">
                  <div className="font-display text-3xl text-slate-900 font-bold">{n}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[520px] lg:h-[620px]">
              <div className="col-span-6 row-span-4 relative overflow-hidden">
                <img
                  src={UK_HERO_IMG}
                  alt="Period London home — Prime Build Design residential builder"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: UK_HERO_OBJECT_POSITION }}
                  loading="eager"
                  decoding="async"
                  width="1200"
                  height="800"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/45 to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <div className="overline text-white/80">Featured</div>
                  <div className="font-display text-2xl font-semibold mt-1">London Period Home</div>
                </div>
              </div>
              <div className="col-span-3 row-span-2 bg-slate-900 flex items-center justify-center p-6">
                <div>
                  <div className="overline text-[#C5A059]">Warranty</div>
                  <div className="text-white font-display text-lg font-semibold mt-1">10-year structural guarantee</div>
                </div>
              </div>
              <div className="col-span-3 row-span-2 overflow-hidden">
                <img
                  src={byId("richmond-kitchen-finished-diner")?.img || byId("richmond-extension-ufh").img}
                  alt="Richmond kitchen extension"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <TrustBadges />

      {/* SERVICES */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <div className="overline mb-4">What we do</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                Seven disciplines.<br /> One uncompromising standard.
              </h2>
            </div>
            <p className="lg:col-span-5 text-slate-600 text-base leading-relaxed">
              Whether you're starting from a plot of land or transforming a family home, we bring the
              same quiet rigour — measured estimates, in-house teams, and weekly progress reports —
              to every project.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {services.map(({ icon: Icon, title, desc, slug }, i) => (
              <Link
                key={title}
                to={`/services/${slug}`}
                className="bg-white p-8 hover:bg-slate-50 transition-colors group"
                data-testid={`home-service-${i}`}
              >
                <Icon size={28} className="text-[#C5A059]" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-2xl font-semibold text-slate-900">{title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
                <span
                  data-testid={`home-service-link-${i}`}
                  className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-slate-900 group-hover:text-[#C5A059] transition-colors"
                >
                  Explore <ArrowUpRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER SLIDER */}
      <BeforeAfterSlider items={beforeAfterItems} />

      {/* PROJECTS */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
            <div>
              <div className="overline mb-4">Selected projects</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">
                A portfolio rooted<br /> in craftsmanship.
              </h2>
            </div>
            <Link to="/projects" className="btn-ghost self-start md:self-end" data-testid="home-view-all-projects">
              View all projects <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {featuredList.map((p, i) => (
              <Link
                key={p.slug || p.id}
                to={`/projects/${p.slug || p.id}`}
                className="group relative h-[420px] overflow-hidden"
                data-testid={`home-project-${i}`}
              >
                <img
                  src={p.img && p.img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.img}` : p.img}
                  alt={p.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  width="800"
                  height="600"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/15 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <div className="overline text-white/70">{p.category} · {p.location}</div>
                  <div className="font-display text-2xl font-semibold mt-1">{p.title}</div>
                  <div className="text-sm text-white/80 mt-2 inline-flex items-center gap-1 group-hover:text-[#C5A059] transition">
                    View project <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* AREAS WE COVER */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <div className="overline mb-4 flex items-center gap-2"><MapPin size={14} className="text-[#C5A059]" /> Where we work</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                Local builders.<br /> Across London.
              </h2>
            </div>
            <p className="lg:col-span-5 text-slate-600 text-base leading-relaxed">
              Based in Wembley Park, we work across some of London's most discerning postcodes —
              with the local planning know-how and considerate-builder credentials each one demands.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-px bg-slate-200">
            {AREAS.map((a) => (
              <Link
                key={a.slug}
                to={`/areas/${a.slug}`}
                data-testid={`home-area-${a.slug}`}
                className="bg-white p-6 hover:bg-slate-50 transition-colors group flex flex-col justify-between min-h-[180px]"
              >
                <div>
                  <div className="overline text-[#C5A059]">{a.code}</div>
                  <div className="font-display text-2xl font-semibold text-slate-900 mt-3">{a.name}</div>
                </div>
                <div className="text-sm font-medium text-slate-700 inline-flex items-center gap-1 mt-4 group-hover:text-[#C5A059] transition-colors">
                  Builders in {a.name} <ArrowUpRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <GoogleReviews />

      {/* FAQ */}
      <FaqSection />

      {/* CTA */}
      <section className="bg-[#C5A059]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight">
            Ready to start your build?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 md:justify-end">
            <Link to="/estimator" className="bg-slate-900 text-white px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-800 transition" data-testid="cta-bottom-estimator">
              Try AI Estimator <ArrowUpRight size={18} />
            </Link>
            <Link to="/contact" className="bg-white text-slate-900 px-6 py-4 font-medium inline-flex items-center gap-2 hover:bg-slate-50 transition" data-testid="cta-bottom-contact">
              Request a Quote <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
